import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";

const POLICIES = {
  privacy: {
    title: "Privacy Policy",
    sub: "How we protect the sleep, data, and privacy of every Dubai Pillows guest.",
    updated: "Last updated: February 2026",
    sections: [
      {
        h: "1. Who we are",
        p: "Dubai Pillows (\"we\", \"us\", \"our\") is a luxury sleep brand headquartered in Dubai, United Arab Emirates. We design, hand-stitch, and ship 7-star hotel-grade goose-down pillows directly to our customers in over 60 countries. This Privacy Policy explains how we collect, use, and safeguard the personal information you share with us when you browse dubaipillows.com, place an order, or contact our concierge team.",
      },
      {
        h: "2. Information we collect",
        p: "When you shop with Dubai Pillows we collect only what we need to deliver an impeccable experience: your name, shipping and billing address, email, phone number (for delivery confirmation), payment details (processed and tokenized by Stripe — we never store full card numbers on our servers), and order history. If you opt in, we may also collect the answers to our sleep-quiz so we can recommend the right firmness and bundle for you.",
      },
      {
        h: "3. How we use your information",
        p: "Your information is used to (a) fulfill, ship, and customs-clear your order, (b) send you transactional updates such as order confirmations, tracking and delivery notifications, (c) provide concierge customer support, (d) honor our 100-night sleep guarantee and any refund requests, and (e) — only if you have opted in — send you curated emails about new launches, exclusive bundles, and sleep science from our team.",
      },
      {
        h: "4. Who we share information with",
        p: "Dubai Pillows does not sell, rent, or trade your personal data. We share strictly what is required with: Stripe (secure payment processing), our logistics partners DHL, FedEx, and Aramex (for door-to-door delivery), our email provider (for transactional and opt-in marketing emails), and analytics providers in aggregated, anonymized form so we can keep improving the site.",
      },
      {
        h: "5. Cookies",
        p: "We use a minimal set of first-party cookies to remember your cart, your selected pillow size, and to measure how well our pages convert. You can disable cookies in your browser, although some checkout features may not work as smoothly. We do not use third-party advertising cookies that track you across the web.",
      },
      {
        h: "6. Your rights",
        p: "You have the right to access, correct, export, or delete the personal data we hold about you at any time. To exercise any of these rights, simply email our concierge team at privacy@dubaipillows.com and we will respond within 7 business days. EU and UK residents are protected under GDPR, California residents under CCPA, and UAE residents under the UAE PDPL — we honour all of these frameworks worldwide.",
      },
      {
        h: "7. Security",
        p: "Our site runs entirely over HTTPS with TLS 1.3 encryption. Payment data is tokenized by Stripe and never touches our servers in plain text. Internal access to customer data is restricted on a need-to-know basis, multi-factor authenticated, and continuously audited.",
      },
      {
        h: "8. Children",
        p: "Dubai Pillows products are not intended for children under 16 and we do not knowingly collect personal data from anyone under that age.",
      },
      {
        h: "9. Changes to this policy",
        p: "We may update this Privacy Policy from time to time. When we make a material change we will update the \"Last updated\" date above and, where appropriate, notify you by email.",
      },
    ],
  },
  refunds: {
    title: "Refund & 100-Night Sleep Trial",
    sub: "Sleep on it for 100 nights. Love it or get every cent back — no questions, no fine print.",
    updated: "Last updated: February 2026",
    sections: [
      {
        h: "1. The 100-night promise",
        p: "Every Dubai Pillow ships with our full 100-night sleep trial. You have one hundred nights from the day your order is delivered to decide whether your pillow has changed your sleep. If it hasn't — for any reason at all — we will refund 100% of what you paid. No restocking fees. No return shipping fees. No questions.",
      },
      {
        h: "2. How to start a refund",
        p: "Email our concierge team at refunds@dubaipillows.com with your order number and a one-line note (we love feedback, but it isn't required). Within 24 business hours you will receive a pre-paid return label and instructions. As soon as the carrier scans the parcel we issue your refund to the original payment method.",
      },
      {
        h: "3. Timing",
        p: "Refunds typically appear in your bank account 3-7 business days after the carrier scan. For PayPal and Apple Pay refunds are usually instant. International refunds may take up to 10 business days depending on your bank.",
      },
      {
        h: "4. What about used pillows?",
        p: "Yes — your pillow can absolutely be slept on. That's the entire point of the 100-night trial. Returned pillows are professionally sanitized and donated to our partner shelters and luxury hotel staff residences across the UAE; they are never re-sold.",
      },
      {
        h: "5. Damaged or wrong items",
        p: "If your order arrives damaged, defective, or different from what you ordered, contact us within 7 days at care@dubaipillows.com with a photo and we will ship a replacement on the next business day at no cost to you.",
      },
      {
        h: "6. Bundles & multi-pillow orders",
        p: "Bundles of 2, 4, or 6 pillows are also covered by the 100-night trial. You may return the entire bundle, or keep a portion and return the rest — we will refund a pro-rated amount based on the bundle discount you originally received.",
      },
      {
        h: "7. Exclusions",
        p: "The 100-night trial does not apply to (a) pillows damaged through clear misuse such as cuts, burns, or pet damage, or (b) pillowcases, sheets, and accessories purchased outside of our pillow range. Those accessories carry a separate 30-day return policy.",
      },
      {
        h: "8. Questions?",
        p: "Our concierge team is reachable 7 days a week. Email refunds@dubaipillows.com or WhatsApp +971 4 000 0000 and a real human will reply within hours.",
      },
    ],
  },
  shipping: {
    title: "Shipping & Delivery",
    sub: "Free, insured, hand-couriered worldwide. From our warehouse to your bedroom — usually in under a week.",
    updated: "Last updated: February 2026",
    sections: [
      {
        h: "1. Free worldwide shipping",
        p: "Every Dubai Pillows order ships free of charge, anywhere in the world. There are no minimum spend requirements, no surprise customs fees, no hidden surcharges. The price you see at checkout is the price you pay — duties and taxes are pre-paid by us on every international order.",
      },
      {
        h: "2. Processing time",
        p: "Each pillow is hand-stitched to order. We process and pack your order in our warehouse within 24–48 hours of purchase (excluding weekends and public holidays). You will receive a tracking number the moment your order leaves our facility.",
      },
      {
        h: "3. Delivery timeframes",
        p: "United States & Canada: 5–7 business days. Europe & United Kingdom: 5–7 business days. Australia & New Zealand: 5–7 business days. Rest of the world: 7–10 business days. All deliveries are fully tracked door-to-door.",
      },
      {
        h: "4. Carriers",
        p: "We ship exclusively with DHL Express, FedEx International Priority, and Aramex Premium. Every parcel is fully insured against loss, theft, and damage in transit and requires a signature on delivery for orders above USD 250.",
      },
      {
        h: "5. Tracking",
        p: "You will receive an email and SMS the moment your pillow is picked up, when it clears customs, when it is out for delivery, and when it is delivered. Real-time tracking is also available 24/7 in your account dashboard at dubaipillows.com/account.",
      },
      {
        h: "6. Customs & duties",
        p: "All duties, VAT, GST, and import taxes are pre-paid by Dubai Pillows for international orders. You will never be asked to pay anything extra at your door. If you are ever charged a fee in error, forward the receipt to shipping@dubaipillows.com and we will reimburse you in full within 48 hours.",
      },
      {
        h: "7. Wrong address or refused delivery",
        p: "If a package is returned to us because the address was incorrect or delivery was refused, we will contact you to arrange re-shipment. A second shipping fee may apply in this case (USD 25 for re-routing). Please double-check your address at checkout.",
      },
      {
        h: "8. P.O. boxes & restricted regions",
        p: "We cannot deliver to P.O. boxes or APO/FPO addresses. If your country is subject to international sanctions we may be unable to ship — please contact us before ordering to confirm.",
      },
    ],
  },
  contact: {
    title: "Contact our Concierge",
    sub: "Real humans. Available 7 days a week. Usually replying in under an hour.",
    updated: "Concierge hours: 8:00–22:00 GST (UTC+4)",
    sections: [
      {
        h: "Email",
        p: "For everything — pre-purchase questions, sizing advice, order updates, returns, press, partnerships — write to care@dubaipillows.com. We reply 7 days a week, typically within 1-3 hours during concierge hours.",
      },
      {
        h: "WhatsApp",
        p: "Prefer to chat? Send us a WhatsApp at +971 4 000 0000. A real member of our Dubai team will reply — no bots, no scripts. Perfect for quick questions about firmness, bundle pricing, or delivery times.",
      },
      {
        h: "Phone",
        p: "Call our concierge line at +971 4 000 0000 during our 8:00–22:00 GST hours (that's 04:00–18:00 UTC, or 23:00–13:00 EST). Outside of those hours, leave a voicemail and we will call you back the next morning.",
      },
      {
        h: "Press & partnerships",
        p: "For press inquiries, hotel partnerships, wholesale, or affiliate programs, please email press@dubaipillows.com with a short brief. We respond to every inquiry within 2 business days.",
      },
      {
        h: "Atelier address",
        p: "Dubai Pillows Atelier · Al Quoz Industrial Area 3 · Dubai · United Arab Emirates. Our atelier is closed to the public — but if you are a VIP customer planning a Dubai visit, email vip@dubaipillows.com to arrange a private tour and fitting.",
      },
      {
        h: "Social",
        p: "Instagram: @dubaipillows · TikTok: @dubaipillows · Facebook: /dubaipillows. DM us anywhere — we read every message.",
      },
    ],
  },
};

export default function Policy() {
  const { slug } = useParams();
  const policy = POLICIES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!policy) return <Navigate to="/" replace />;

  return (
    <main
      data-testid={`policy-page-${slug}`}
      className="min-h-screen bg-[#f5f4f1] text-[#0e0e10]"
    >
      <Seo
        title={`${policy.title} · Dubai Pillows`}
        description={policy.sub}
        url={`https://goose-down-elite.emergent.host/policies/${slug}`}
        keywords={`dubai pillows ${slug}, luxury pillow ${slug}`}
      />
      <header className="border-b border-black/5 bg-white/70 backdrop-blur sticky top-0 z-30">
        <div className="container-x py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[13px] font-bold tracking-wide hover:opacity-70"
            data-testid="policy-back-home"
          >
            <ArrowLeft size={16} />
            BACK TO STORE
          </Link>
          <img
            src="/assets/dubai_logo_black.png"
            alt="Dubai Pillows"
            className="h-9 w-auto"
          />
        </div>
      </header>

      <section className="container-x pt-16 md:pt-24 pb-10">
        <div className="max-w-3xl">
          <div className="inline-block bg-[#d8f06b] text-[#0e0e10] text-[11px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 rounded-full">
            Policies
          </div>
          <h1
            data-testid="policy-title"
            className="mt-5 text-[40px] sm:text-[56px] md:text-[72px] leading-[1.02] font-black tracking-[-0.025em]"
          >
            {policy.title}
          </h1>
          <p className="mt-5 text-[16px] md:text-[18px] text-[#3a3a40] max-w-2xl leading-relaxed">
            {policy.sub}
          </p>
          <div className="mt-3 text-[12px] uppercase tracking-[0.18em] text-[#6b6b72] font-semibold">
            {policy.updated}
          </div>
        </div>
      </section>

      <section className="container-x pb-24">
        <div className="grid md:grid-cols-[260px_1fr] gap-10 md:gap-16">
          <aside className="md:sticky md:top-24 self-start">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#6b6b72] mb-3">
              Browse all
            </div>
            <nav className="flex flex-col gap-2">
              {Object.entries(POLICIES).map(([key, p]) => (
                <Link
                  key={key}
                  to={`/policies/${key}`}
                  data-testid={`policy-nav-${key}`}
                  className={`text-[14px] px-4 py-2.5 rounded-full border transition ${
                    key === slug
                      ? "bg-[#0e0e10] text-white border-[#0e0e10]"
                      : "bg-white text-[#0e0e10] border-black/10 hover:border-[#0e0e10]"
                  }`}
                >
                  {p.title}
                </Link>
              ))}
            </nav>
          </aside>

          <article className="max-w-2xl">
            <div className="space-y-8">
              {policy.sections.map((s) => (
                <div key={s.h}>
                  <h2 className="text-[20px] md:text-[24px] font-black tracking-[-0.01em] mb-2">
                    {s.h}
                  </h2>
                  <p className="text-[15px] md:text-[16px] leading-[1.7] text-[#2a2a2e]">
                    {s.p}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-14 p-6 md:p-8 rounded-3xl bg-[#0e0e10] text-white">
              <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-bold mb-2">
                Still have questions?
              </div>
              <div className="text-[20px] md:text-[24px] font-black tracking-[-0.01em] mb-3">
                Our Dubai concierge is one message away.
              </div>
              <p className="text-white/70 text-[14px] mb-5 leading-relaxed">
                Email <span className="text-[#d8f06b]">care@dubaipillows.com</span> or WhatsApp <span className="text-[#d8f06b]">+971 4 000 0000</span>. Real humans, real fast.
              </p>
              <Link
                to="/"
                data-testid="policy-cta-shop"
                className="inline-flex items-center justify-center bg-[#d8f06b] text-[#0e0e10] font-bold text-[13px] uppercase tracking-[0.12em] rounded-full px-6 py-3 hover:bg-white transition"
              >
                Shop the pillow
              </Link>
            </div>
          </article>
        </div>
      </section>
    </main>
  );
}
